import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Pen, RotateCcw, Send, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CSEN_COUNTRIES } from "@/data/countries";
import { ITALIAN_PROVINCES } from "@/data/italianCities";
import CityCombobox from "@/components/CityCombobox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Regolamento content
const regulationsContent = {
  it: {
    title: "Regolamento Porto Pollo Academy A.S.D.",
    rules: [
      {
        number: 1,
        text: "Per le lezioni sei tenuto ad arrivare 15 minuti prima della lezione per iniziare in orario. Motivo: prepararsi in tempo per la lezione."
      },
      {
        number: 2,
        text: "La lezione inizia quando l'attrezzatura viene data."
      },
      {
        number: 3,
        text: "In caso il team ti confermi la lezione, solitamente chiede un deposito o l'intero importo della lezione. RICORDA che la cancellazione o lo spostamento della lezione deve essere comunicato ENTRO LE ORE 18:00 DEL GIORNO PRIMA. PER NESSUN MOTIVO il deposito o la lezione può essere rimborsato in caso di mancata comunicazione entro questo termine. Questo lo specifichiamo in caso il team ti confermi la lezione: la cancellazione può essere fatta solo dalla scuola, anche poco prima della lezione in caso di mancato vento o altri motivi. In quel caso verrà riaccreditato l'intero importo pagato o programmata un'altra lezione."
      },
      {
        number: 4,
        text: "Le lezioni SEMI-PRIVATE sono con 1 attrezzatura alternata tra i partecipanti. Le lezioni di GRUPPO sono con più attrezzature (ognuno ha la sua). IMPORTANTE: La prima lezione di kitesurf NON è con la tavola ai piedi - si inizia con il controllo dell'aquilone a terra e in acqua (body drag)."
      },
      {
        number: 5,
        text: "L'istruttore viene SCELTO DALLA SCUOLA in base alla disponibilità. Non è garantito che sia sempre lo stesso istruttore. Se desideri cambiare istruttore per la lezione successiva, puoi richiederlo e faremo il possibile per accontentarti."
      }
    ],
    close: "Chiudi"
  },
  en: {
    title: "Porto Pollo Academy A.S.D. Regulations",
    rules: [
      {
        number: 1,
        text: "You are required to arrive 15 minutes before the lesson to start on time. Reason: to prepare in time for the lesson."
      },
      {
        number: 2,
        text: "The lesson starts when the equipment is handed over."
      },
      {
        number: 3,
        text: "If the team confirms your lesson, they usually ask for a deposit or the full lesson amount. REMEMBER that cancellation or rescheduling must be communicated BY 6:00 PM THE DAY BEFORE. FOR NO REASON can the deposit or lesson be refunded if not communicated by this deadline. We specify this in case the team confirms your lesson: cancellation can only be made by the school, even shortly before the lesson in case of no wind or other reasons. In that case, the full amount paid will be refunded or another lesson will be scheduled."
      },
      {
        number: 4,
        text: "SEMI-PRIVATE lessons use 1 equipment alternated between participants. GROUP lessons have multiple equipment (each person has their own). IMPORTANT: The first kitesurf lesson is NOT with the board on your feet - you start with kite control on land and in the water (body drag)."
      },
      {
        number: 5,
        text: "The instructor is CHOSEN BY THE SCHOOL based on availability. It is not guaranteed to always be the same instructor. If you wish to change instructor for your next lesson, you can request it and we will do our best to accommodate you."
      }
    ],
    close: "Close"
  },
  fr: {
    title: "Règlement Porto Pollo Academy A.S.D.",
    rules: [
      {
        number: 1,
        text: "Vous devez arriver 15 minutes avant le cours pour commencer à l'heure. Raison: se préparer à temps pour le cours."
      },
      {
        number: 2,
        text: "Le cours commence lorsque l'équipement est remis."
      },
      {
        number: 3,
        text: "Si l'équipe confirme votre cours, elle demande généralement un acompte ou le montant total. RAPPELEZ-VOUS que l'annulation ou le report doit être communiqué AVANT 18H00 LA VEILLE. EN AUCUN CAS l'acompte ou le cours ne peut être remboursé si non communiqué avant ce délai. L'annulation ne peut être faite que par l'école, même peu avant le cours en cas de manque de vent ou autres raisons. Dans ce cas, le montant sera remboursé ou un autre cours sera programmé."
      },
      {
        number: 4,
        text: "Les cours SEMI-PRIVÉS utilisent 1 équipement alterné entre les participants. Les cours de GROUPE ont plusieurs équipements (chacun a le sien). IMPORTANT: Le premier cours de kitesurf n'est PAS avec la planche aux pieds - on commence par le contrôle de l'aile au sol et dans l'eau (body drag)."
      },
      {
        number: 5,
        text: "L'instructeur est CHOISI PAR L'ÉCOLE selon la disponibilité. Il n'est pas garanti que ce soit toujours le même instructeur. Si vous souhaitez changer d'instructeur pour votre prochain cours, vous pouvez le demander et nous ferons de notre mieux pour vous satisfaire."
      }
    ],
    close: "Fermer"
  },
  de: {
    title: "Regeln Porto Pollo Academy A.S.D.",
    rules: [
      {
        number: 1,
        text: "Sie müssen 15 Minuten vor der Lektion ankommen, um pünktlich zu beginnen. Grund: sich rechtzeitig auf die Lektion vorzubereiten."
      },
      {
        number: 2,
        text: "Die Lektion beginnt, wenn die Ausrüstung übergeben wird."
      },
      {
        number: 3,
        text: "Wenn das Team Ihre Lektion bestätigt, wird normalerweise eine Anzahlung oder der volle Betrag verlangt. DENKEN SIE DARAN: Stornierung oder Verschiebung muss BIS 18:00 UHR AM VORTAG mitgeteilt werden. AUS KEINEM GRUND kann die Anzahlung oder Lektion erstattet werden, wenn nicht rechtzeitig vor dieser Frist kommuniziert. Die Stornierung kann nur von der Schule vorgenommen werden, auch kurz vor der Lektion bei fehlendem Wind oder anderen Gründen. In diesem Fall wird der Betrag erstattet oder eine andere Lektion geplant."
      },
      {
        number: 4,
        text: "HALB-PRIVATE Lektionen verwenden 1 Ausrüstung, die zwischen den Teilnehmern gewechselt wird. GRUPPEN-Lektionen haben mehrere Ausrüstungen (jeder hat seine eigene). WICHTIG: Die erste Kitesurfstunde ist NICHT mit dem Brett an den Füßen - man beginnt mit der Kitekontrolle am Strand und im Wasser (Body Drag)."
      },
      {
        number: 5,
        text: "Der Lehrer wird VON DER SCHULE GEWÄHLT je nach Verfügbarkeit. Es ist nicht garantiert, dass es immer derselbe Lehrer ist. Wenn Sie für Ihre nächste Stunde einen anderen Lehrer wünschen, können Sie dies anfragen und wir werden unser Bestes tun."
      }
    ],
    close: "Schließen"
  },
  es: {
    title: "Reglamento Porto Pollo Academy A.S.D.",
    rules: [
      {
        number: 1,
        text: "Debes llegar 15 minutos antes de la lección para empezar a tiempo. Motivo: prepararse a tiempo para la lección."
      },
      {
        number: 2,
        text: "La lección comienza cuando se entrega el equipo."
      },
      {
        number: 3,
        text: "Si el equipo confirma tu lección, generalmente pide un depósito o el importe completo. RECUERDA que la cancelación o reprogramación debe comunicarse ANTES DE LAS 18:00 DEL DÍA ANTERIOR. POR NINGÚN MOTIVO se puede reembolsar el depósito o la lección si no se comunica antes de este plazo. La cancelación solo puede ser hecha por la escuela, incluso poco antes de la lección en caso de falta de viento u otros motivos. En ese caso, se reembolsará el importe o se programará otra lección."
      },
      {
        number: 4,
        text: "Las lecciones SEMI-PRIVADAS usan 1 equipo alternado entre los participantes. Las lecciones de GRUPO tienen varios equipos (cada uno tiene el suyo). IMPORTANTE: La primera lección de kitesurf NO es con la tabla en los pies - se empieza con el control de la cometa en tierra y en el agua (body drag)."
      },
      {
        number: 5,
        text: "El instructor es ELEGIDO POR LA ESCUELA según la disponibilidad. No está garantizado que sea siempre el mismo instructor. Si deseas cambiar de instructor para tu próxima lección, puedes solicitarlo y haremos lo posible para complacerte."
      }
    ],
    close: "Cerrar"
  }
};

// Translations for the form
const formTranslations = {
  it: {
    title: "Modulo di Iscrizione",
    subtitle: "Circolo Portopollo Academy A.S.D.",
    step1Title: "Dati Personali",
    step2Title: "Tipo di Lezione",
    step3Title: "Firma il Modulo",
    name: "Nome",
    surname: "Cognome",
    email: "Email",
    phone: "Telefono",
    fiscalCode: "Codice Fiscale / ID",
    birthDate: "Data di Nascita",
    birthPlace: "Luogo di Nascita",
    residenceCity: "Città di Residenza",
    residenceAddress: "Indirizzo",
    activity: "Attività",
    numParticipants: "Numero partecipanti",
    participantAge: "Età",
    participantWeight: "Peso (kg)",
    agePlaceholder: "Es: 25",
    weightPlaceholder: "Es: 70",
    level: "Livello",
    levelBeginner: "Completamente principiante",
    levelExperienced: "Ho già fatto qualcosa",
    levelDetails: "Specifica la tua esperienza...",
    preferredDate: "Data Preferita",
    time: "Orario",
    selectTime: "Scegli",
    duration: "Durata",
    hour1: "1 ora",
    hours2: "2 ore private - €130",
    hours2semi: "2 ore semi-private - €90 a testa",
    hours6: "Corso base 6 ore (2-3 giorni)",
    // Activity translations
    windsurfPrivate: "🌊 Windsurf Privato",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privato",
    wingfoilPrivate: "🪽 Wingfoil Privato",
    wingfoilGroup: "🪽 Wingfoil Gruppo",
    wingsurfPrivate: "🏄 Wingsurf Privato",
    wingsurfGroup: "🏄 Wingsurf Gruppo",
    kitesurfPrivate: "🪁 Kitesurf Privato",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privato",
    // Duration translations
    duration_2h: "2 ore",
    duration_6h: "Corso base 6 ore",
    duration_1h: "1 ora",
    perPerson: "a testa",
    regIncluded: "iscriz. €10 inclusa",
    // Multi-day stay translations
    stayMultipleDays: "Voglio rimanere più giorni",
    stayDuration: "Quanto tempo rimani?",
    stay2to6days: "2-6 giorni",
    stay1week: "1 settimana",
    stay2weeks: "2+ settimane",
    additionalDates: "Seleziona altre date disponibili",
    accommodationShort: "🏠 Per alloggio: isoladeigabbiani.it (camping), hotelportopuddu.it (residence), hotelledune.it (albergo)",
    accommodationLong: "🏡 Puoi prenotare la nostra casa in campagna a 15 min dalla scuola!",
    accommodationPrices: "💰 Prezzi camera privata: €60/giorno (bassa stagione Apr-Mag-Set-Ott) | €100/giorno (alta stagione). Casa intera (max 6 persone): €200/giorno bassa stagione | €400/giorno alta stagione. Min. 7 giorni.",
    accommodationLink: "ℹ️ Info: portopollo.it",
    declarationsTitle: "Dichiarazioni Obbligatorie",
    healthDeclaration: "Dichiaro di essere in buone condizioni fisiche e di essere in possesso di un certificato medico per la pratica sportiva non agonistica.",
    regulationsDeclaration: "Dichiaro di conoscere e accettare il regolamento dell'associazione Porto Pollo Academy A.S.D.",
    liabilityDeclaration: "Sollevo Porto Pollo Academy A.S.D. da ogni responsabilità per danni a terzi o a me stesso durante la pratica dell'attività.",
    privacyDeclaration: "Acconsento al trattamento dei dati personali ai sensi del D.Lgs 196/2003 e GDPR.",
    optionalTitle: "Consensi Opzionali (non obbligatori)",
    mediaConsent: "📸 Acconsento ad essere ripreso/fotografato e che le immagini possano essere utilizzate sui canali social e web della scuola.",
    newsletterConsent: "📧 Desidero ricevere newsletter e aggiornamenti dalla scuola.",
    membershipFee: "✅ Quota associativa €10 già inclusa nel prezzo",
    rentalUpsellTitle: "Noleggio Attrezzatura (opzionale)",
    rentalUpsellDesc: "Aggiungi 1 ora di noleggio attrezzatura dopo la lezione a prezzo scontato!",
    rentalUpsellPrice: "€20/attrezzatura anziche' €30",
    rentalUpsellAdd: "Aggiungi noleggio",
    rentalEquipmentCount: "Quante attrezzature vuoi noleggiare?",
    rentalEquipmentSingle: "attrezzatura",
    rentalEquipmentPlural: "attrezzature",
    scheduleWarning: "⚠️ Nota importante: Gli orari e i giorni indicati sono preferenze e potrebbero variare in base alle prenotazioni esistenti e alle condizioni del vento. Ti confermeremo data e orario definitivi.",
    signatureInstructions: "Usa il dito o il mouse per firmare nel riquadro sottostante.",
    clearSignature: "Cancella Firma",
    signHere: "Firma qui",
    signatureNote: "Nota: Firmando questo modulo accetti tutte le condizioni indicate e confermi la veridicità dei dati inseriti.",
    back: "Indietro",
    next: "Avanti",
    submit: "Invia Modulo",
    sending: "Invio...",
    successMessage: "Modulo inviato con successo! Ti contatteremo presto 🏄‍♀️",
    errorRequired: "Compila tutti i campi obbligatori",
    errorDeclarations: "Devi accettare tutte le dichiarazioni",
    errorSignature: "Firma il modulo per procedere",
    errorSubmit: "Errore nell'invio del modulo. Riprova.",
    minorSectionTitle: "IN CASO DI MINORE — Dati Genitore/Tutore",
    minorWarning: "Il partecipante ha meno di 18 anni — i dati del genitore/tutore sono obbligatori.",
    parentDataLabel: "Dati del Genitore/Tutore",
    nationality: "Nazionalita'",
    birthDateShort: "Data nascita",
    birthPlaceShort: "Luogo nascita",
    provinceShort: "Prov.",
    sex: "Sesso",
    male: "Maschio",
    female: "Femmina",
    calcFiscalCode: "Calcola",
    address: "Indirizzo",
    cap: "CAP",
    cityShort: "Comune",
    phoneShort: "Tel/Cell"
  },
  en: {
    title: "Registration Form",
    subtitle: "Circolo Portopollo Academy A.S.D.",
    step1Title: "Personal Data",
    step2Title: "Lesson Type",
    step3Title: "Sign the Form",
    name: "First Name",
    surname: "Last Name",
    email: "Email",
    phone: "Phone",
    fiscalCode: "Tax Code / ID",
    birthDate: "Date of Birth",
    birthPlace: "Place of Birth",
    residenceCity: "City of Residence",
    residenceAddress: "Address",
    activity: "Activity",
    numParticipants: "Number of participants",
    participantAge: "Age",
    participantWeight: "Weight (kg)",
    agePlaceholder: "E.g.: 25",
    weightPlaceholder: "E.g.: 70",
    level: "Level",
    levelBeginner: "Complete beginner",
    levelExperienced: "I have some experience",
    levelDetails: "Describe your experience...",
    preferredDate: "Preferred Date",
    time: "Time",
    selectTime: "Select",
    duration: "Duration",
    hour1: "1 hour",
    hours2: "2 hours private - €130",
    hours2semi: "2 hours semi-private - €90 each",
    hours6: "Basic course 6 hours (2-3 days)",
    // Activity translations
    windsurfPrivate: "🌊 Windsurf Private",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Private",
    wingfoilPrivate: "🪽 Wingfoil Private",
    wingfoilGroup: "🪽 Wingfoil Group",
    wingsurfPrivate: "🏄 Wingsurf Private",
    wingsurfGroup: "🏄 Wingsurf Group",
    kitesurfPrivate: "🪁 Kitesurf Private",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Private",
    // Duration translations
    duration_2h: "2 hours",
    duration_6h: "Basic course 6 hours",
    duration_1h: "1 hour",
    perPerson: "each",
    regIncluded: "reg. €10 included",
    // Multi-day stay translations
    stayMultipleDays: "I want to stay multiple days",
    stayDuration: "How long are you staying?",
    stay2to6days: "2-6 days",
    stay1week: "1 week",
    stay2weeks: "2+ weeks",
    additionalDates: "Select other available dates",
    accommodationShort: "🏠 For accommodation: isoladeigabbiani.it (camping), hotelportopuddu.it (residence), hotelledune.it (hotel)",
    accommodationLong: "🏡 You can book our countryside house 15 min from the school!",
    accommodationPrices: "💰 Private room: €60/day (low season Apr-May-Sep-Oct) | €100/day (high season). Whole house (max 6 people): €200/day low season | €400/day high season. Min. 7 days.",
    accommodationLink: "ℹ️ Info: portopollo.it",
    declarationsTitle: "Mandatory Declarations",
    healthDeclaration: "I declare that I am in good physical condition and in possession of a medical certificate for non-competitive sports practice.",
    regulationsDeclaration: "I declare that I know and accept the regulations of Porto Pollo Academy A.S.D.",
    liabilityDeclaration: "I release Porto Pollo Academy A.S.D. from any liability for damages to third parties or myself during the activity.",
    privacyDeclaration: "I consent to the processing of personal data pursuant to Legislative Decree 196/2003 and GDPR.",
    optionalTitle: "Optional Consents (not mandatory)",
    mediaConsent: "📸 I consent to being filmed/photographed and that images may be used on the school's social media and web channels.",
    newsletterConsent: "📧 I would like to receive newsletters and updates from the school.",
    membershipFee: "✅ Membership fee €10 already included in the price",
    rentalUpsellTitle: "Equipment Rental (optional)",
    rentalUpsellDesc: "Add 1 hour of equipment rental after your lesson at a discounted price!",
    rentalUpsellPrice: "€20/equipment instead of €30",
    rentalUpsellAdd: "Add rental",
    rentalEquipmentCount: "How many equipment sets do you want to rent?",
    rentalEquipmentSingle: "equipment",
    rentalEquipmentPlural: "equipment sets",
    scheduleWarning: "⚠️ Important note: The indicated times and days are preferences and may vary based on existing bookings and wind conditions. We will confirm the final date and time.",
    signatureInstructions: "Use your finger or mouse to sign in the box below.",
    clearSignature: "Clear Signature",
    signHere: "Sign here",
    signatureNote: "Note: By signing this form, you accept all the conditions indicated and confirm the accuracy of the data entered.",
    back: "Back",
    next: "Next",
    submit: "Submit Form",
    sending: "Sending...",
    successMessage: "Form submitted successfully! We will contact you soon 🏄‍♀️",
    errorRequired: "Please fill in all required fields",
    errorDeclarations: "You must accept all declarations",
    errorSignature: "Please sign the form to proceed",
    errorSubmit: "Error submitting the form. Please try again.",
    minorSectionTitle: "IF MINOR — Parent/Guardian Data",
    minorWarning: "The participant is under 18 — parent/guardian data is required.",
    parentDataLabel: "Parent/Guardian Data",
    nationality: "Nationality",
    birthDateShort: "Birth date",
    birthPlaceShort: "Birth place",
    provinceShort: "Prov.",
    sex: "Sex",
    male: "Male",
    female: "Female",
    calcFiscalCode: "Calculate",
    address: "Address",
    cap: "ZIP",
    cityShort: "City",
    phoneShort: "Tel/Mobile"
  },
  fr: {
    title: "Formulaire d'inscription",
    subtitle: "Circolo Portopollo Academy A.S.D.",
    step1Title: "Données Personnelles",
    step2Title: "Type de Cours",
    step3Title: "Signer le Formulaire",
    name: "Prénom",
    surname: "Nom",
    email: "Email",
    phone: "Téléphone",
    fiscalCode: "Code Fiscal / ID",
    birthDate: "Date de Naissance",
    birthPlace: "Lieu de Naissance",
    residenceCity: "Ville de Résidence",
    residenceAddress: "Adresse",
    activity: "Activité",
    numParticipants: "Nombre de participants",
    participantAge: "Âge",
    participantWeight: "Poids (kg)",
    agePlaceholder: "Ex: 25",
    weightPlaceholder: "Ex: 70",
    level: "Niveau",
    levelBeginner: "Complètement débutant",
    levelExperienced: "J'ai déjà pratiqué",
    levelDetails: "Décrivez votre expérience...",
    preferredDate: "Date Préférée",
    time: "Horaire",
    selectTime: "Choisir",
    duration: "Durée",
    hour1: "1 heure",
    hours2: "2 heures privées - 130€",
    hours2semi: "2 heures semi-privées - 90€ chacun",
    hours6: "Cours de base 6 heures (2-3 jours)",
    // Activity translations
    windsurfPrivate: "🌊 Windsurf Privé",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privé",
    wingfoilPrivate: "🪽 Wingfoil Privé",
    wingfoilGroup: "🪽 Wingfoil Groupe",
    wingsurfPrivate: "🏄 Wingsurf Privé",
    wingsurfGroup: "🏄 Wingsurf Groupe",
    kitesurfPrivate: "🪁 Kitesurf Privé",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privé",
    // Duration translations
    duration_2h: "2 heures",
    duration_6h: "Cours de base 6 heures",
    duration_1h: "1 heure",
    perPerson: "chacun",
    regIncluded: "inscr. €10 incluse",
    // Multi-day stay translations
    stayMultipleDays: "Je veux rester plusieurs jours",
    stayDuration: "Combien de temps restez-vous?",
    stay2to6days: "2-6 jours",
    stay1week: "1 semaine",
    stay2weeks: "2+ semaines",
    additionalDates: "Sélectionnez d'autres dates disponibles",
    accommodationShort: "🏠 Pour l'hébergement: isoladeigabbiani.it (camping), hotelportopuddu.it (résidence), hotelledune.it (hôtel)",
    accommodationLong: "🏡 Vous pouvez réserver notre maison de campagne à 15 min de l'école!",
    accommodationPrices: "💰 Chambre privée: €60/jour (basse saison Avr-Mai-Sep-Oct) | €100/jour (haute saison). Maison entière (max 6 pers.): €200/jour basse saison | €400/jour haute saison. Min. 7 jours.",
    accommodationLink: "ℹ️ Info: portopollo.it",
    declarationsTitle: "Déclarations Obligatoires",
    healthDeclaration: "Je déclare être en bonne condition physique et être en possession d'un certificat médical pour la pratique sportive non compétitive.",
    regulationsDeclaration: "Je déclare connaître et accepter le règlement de l'association Porto Pollo Academy A.S.D.",
    liabilityDeclaration: "Je décharge Porto Pollo Academy A.S.D. de toute responsabilité pour les dommages causés à des tiers ou à moi-même pendant l'activité.",
    privacyDeclaration: "Je consens au traitement des données personnelles conformément au décret législatif 196/2003 et au RGPD.",
    optionalTitle: "Consentements Optionnels (non obligatoires)",
    mediaConsent: "📸 J'accepte d'être filmé/photographié et que les images soient utilisées sur les réseaux sociaux et le site web de l'école.",
    newsletterConsent: "📧 Je souhaite recevoir des newsletters et mises à jour de l'école.",
    membershipFee: "✅ Cotisation €10 déjà incluse dans le prix",
    rentalUpsellTitle: "Location d'Equipement (optionnel)",
    rentalUpsellDesc: "Ajoutez 1 heure de location d'equipement apres votre cours a prix reduit!",
    rentalUpsellPrice: "€20/equipement au lieu de €30",
    rentalUpsellAdd: "Ajouter la location",
    rentalEquipmentCount: "Combien d'equipements voulez-vous louer?",
    rentalEquipmentSingle: "equipement",
    rentalEquipmentPlural: "equipements",
    scheduleWarning: "⚠️ Note importante: Les horaires et jours indiqués sont des préférences et peuvent varier en fonction des réservations existantes et des conditions de vent. Nous vous confirmerons la date et l'heure définitives.",
    signatureInstructions: "Utilisez votre doigt ou la souris pour signer dans le cadre ci-dessous.",
    clearSignature: "Effacer la Signature",
    signHere: "Signez ici",
    signatureNote: "Note: En signant ce formulaire, vous acceptez toutes les conditions indiquées et confirmez l'exactitude des données saisies.",
    back: "Retour",
    next: "Suivant",
    submit: "Envoyer le Formulaire",
    sending: "Envoi...",
    successMessage: "Formulaire envoyé avec succès! Nous vous contacterons bientôt 🏄‍♀️",
    errorRequired: "Veuillez remplir tous les champs obligatoires",
    errorDeclarations: "Vous devez accepter toutes les déclarations",
    errorSignature: "Veuillez signer le formulaire pour continuer",
    errorSubmit: "Erreur lors de l'envoi du formulaire. Veuillez réessayer.",
    minorSectionTitle: "EN CAS DE MINEUR — Données du Parent/Tuteur",
    minorWarning: "Le participant a moins de 18 ans — les données du parent/tuteur sont obligatoires.",
    parentDataLabel: "Données du Parent/Tuteur",
    nationality: "Nationalité",
    birthDateShort: "Date de naissance",
    birthPlaceShort: "Lieu de naissance",
    provinceShort: "Prov.",
    sex: "Sexe",
    male: "Homme",
    female: "Femme",
    calcFiscalCode: "Calculer",
    address: "Adresse",
    cap: "CP",
    cityShort: "Commune",
    phoneShort: "Tel/Mobile"
  },
  de: {
    title: "Anmeldeformular",
    subtitle: "Circolo Portopollo Academy A.S.D.",
    step1Title: "Persönliche Daten",
    step2Title: "Unterrichtsart",
    step3Title: "Formular Unterschreiben",
    name: "Vorname",
    surname: "Nachname",
    email: "E-Mail",
    phone: "Telefon",
    fiscalCode: "Steuernummer / ID",
    birthDate: "Geburtsdatum",
    birthPlace: "Geburtsort",
    residenceCity: "Wohnort",
    residenceAddress: "Adresse",
    activity: "Aktivität",
    numParticipants: "Anzahl der Teilnehmer",
    participantAge: "Alter",
    participantWeight: "Gewicht (kg)",
    agePlaceholder: "Z.B.: 25",
    weightPlaceholder: "Z.B.: 70",
    level: "Niveau",
    levelBeginner: "Kompletter Anfänger",
    levelExperienced: "Ich habe schon Erfahrung",
    levelDetails: "Beschreiben Sie Ihre Erfahrung...",
    preferredDate: "Bevorzugtes Datum",
    time: "Uhrzeit",
    selectTime: "Wählen",
    duration: "Dauer",
    hour1: "1 Stunde",
    hours2: "2 Stunden privat - 130€",
    hours2semi: "2 Stunden halb-privat - 90€ pro Person",
    hours6: "Grundkurs 6 Stunden (2-3 Tage)",
    // Activity translations
    windsurfPrivate: "🌊 Windsurf Privat",
    windsurfSemiPrivate: "🌊 Windsurf Halb-Privat",
    wingfoilPrivate: "🪽 Wingfoil Privat",
    wingfoilGroup: "🪽 Wingfoil Gruppe",
    wingsurfPrivate: "🏄 Wingsurf Privat",
    wingsurfGroup: "🏄 Wingsurf Gruppe",
    kitesurfPrivate: "🪁 Kitesurf Privat",
    kitesurfSemiPrivate: "🪁 Kitesurf Halb-Privat",
    // Duration translations
    duration_2h: "2 Stunden",
    duration_6h: "Grundkurs 6 Stunden",
    duration_1h: "1 Stunde",
    perPerson: "pro Person",
    regIncluded: "Anm. €10 inkl.",
    // Multi-day stay translations
    stayMultipleDays: "Ich möchte mehrere Tage bleiben",
    stayDuration: "Wie lange bleiben Sie?",
    stay2to6days: "2-6 Tage",
    stay1week: "1 Woche",
    stay2weeks: "2+ Wochen",
    additionalDates: "Wählen Sie weitere verfügbare Daten",
    accommodationShort: "🏠 Für Unterkunft: isoladeigabbiani.it (Camping), hotelportopuddu.it (Residence), hotelledune.it (Hotel)",
    accommodationLong: "🏡 Sie können unser Landhaus 15 Min. von der Schule buchen!",
    accommodationPrices: "💰 Privatzimmer: €60/Tag (Nebensaison Apr-Mai-Sep-Okt) | €100/Tag (Hochsaison). Ganzes Haus (max 6 Pers.): €200/Tag Nebensaison | €400/Tag Hochsaison. Min. 7 Tage.",
    accommodationLink: "ℹ️ Info: portopollo.it",
    declarationsTitle: "Pflichtangaben",
    healthDeclaration: "Ich erkläre, dass ich in guter körperlicher Verfassung bin und im Besitz eines ärztlichen Attests für nicht-wettkampfmäßigen Sport bin.",
    regulationsDeclaration: "Ich erkläre, dass ich die Regeln des Vereins Porto Pollo Academy A.S.D. kenne und akzeptiere.",
    liabilityDeclaration: "Ich entlaste Porto Pollo Academy A.S.D. von jeglicher Haftung für Schäden an Dritten oder mir selbst während der Aktivität.",
    privacyDeclaration: "Ich stimme der Verarbeitung personenbezogener Daten gemäß dem Gesetzesdekret 196/2003 und der DSGVO zu.",
    optionalTitle: "Optionale Zustimmungen (nicht verpflichtend)",
    mediaConsent: "📸 Ich stimme zu, gefilmt/fotografiert zu werden und dass die Bilder auf den Social-Media- und Webkanälen der Schule verwendet werden dürfen.",
    newsletterConsent: "📧 Ich möchte Newsletter und Updates von der Schule erhalten.",
    membershipFee: "✅ Mitgliedsbeitrag €10 bereits im Preis enthalten",
    rentalUpsellTitle: "Ausrustungsverleih (optional)",
    rentalUpsellDesc: "Fugen Sie 1 Stunde Ausrustungsverleih nach Ihrer Stunde zum Sonderpreis hinzu!",
    rentalUpsellPrice: "€20/Ausrustung statt €30",
    rentalUpsellAdd: "Verleih hinzufugen",
    rentalEquipmentCount: "Wie viele Ausrustungen mochten Sie mieten?",
    rentalEquipmentSingle: "Ausrustung",
    rentalEquipmentPlural: "Ausrustungen",
    scheduleWarning: "⚠️ Wichtiger Hinweis: Die angegebenen Zeiten und Tage sind Präferenzen und können je nach bestehenden Buchungen und Windbedingungen variieren. Wir werden das endgültige Datum und die Uhrzeit bestätigen.",
    signatureInstructions: "Verwenden Sie Ihren Finger oder die Maus, um im untenstehenden Feld zu unterschreiben.",
    clearSignature: "Unterschrift Löschen",
    signHere: "Hier unterschreiben",
    signatureNote: "Hinweis: Mit Ihrer Unterschrift akzeptieren Sie alle angegebenen Bedingungen und bestätigen die Richtigkeit der eingegebenen Daten.",
    back: "Zurück",
    next: "Weiter",
    submit: "Formular Absenden",
    sending: "Senden...",
    successMessage: "Formular erfolgreich gesendet! Wir werden Sie bald kontaktieren 🏄‍♀️",
    errorRequired: "Bitte füllen Sie alle Pflichtfelder aus",
    errorDeclarations: "Sie müssen alle Erklärungen akzeptieren",
    errorSignature: "Bitte unterschreiben Sie das Formular, um fortzufahren",
    errorSubmit: "Fehler beim Senden des Formulars. Bitte versuchen Sie es erneut.",
    minorSectionTitle: "BEI MINDERJÄHRIGEN — Daten des Erziehungsberechtigten",
    minorWarning: "Der Teilnehmer ist unter 18 — Daten des Erziehungsberechtigten sind erforderlich.",
    parentDataLabel: "Daten des Erziehungsberechtigten",
    nationality: "Nationalität",
    birthDateShort: "Geburtsdatum",
    birthPlaceShort: "Geburtsort",
    provinceShort: "Prov.",
    sex: "Geschlecht",
    male: "Männlich",
    female: "Weiblich",
    calcFiscalCode: "Berechnen",
    address: "Adresse",
    cap: "PLZ",
    cityShort: "Stadt",
    phoneShort: "Tel/Mobil"
  },
  es: {
    title: "Formulario de Inscripción",
    subtitle: "Circolo Portopollo Academy A.S.D.",
    step1Title: "Datos Personales",
    step2Title: "Tipo de Clase",
    step3Title: "Firmar el Formulario",
    name: "Nombre",
    surname: "Apellido",
    email: "Correo Electrónico",
    phone: "Teléfono",
    fiscalCode: "Código Fiscal / ID",
    birthDate: "Fecha de Nacimiento",
    birthPlace: "Lugar de Nacimiento",
    residenceCity: "Ciudad de Residencia",
    residenceAddress: "Dirección",
    activity: "Actividad",
    numParticipants: "Número de participantes",
    participantAge: "Edad",
    participantWeight: "Peso (kg)",
    agePlaceholder: "Ej: 25",
    weightPlaceholder: "Ej: 70",
    level: "Nivel",
    levelBeginner: "Completamente principiante",
    levelExperienced: "Ya tengo experiencia",
    levelDetails: "Describe tu experiencia...",
    preferredDate: "Fecha Preferida",
    time: "Horario",
    selectTime: "Elegir",
    duration: "Duración",
    hour1: "1 hora",
    hours2: "2 horas privadas - 130€",
    hours2semi: "2 horas semi-privadas - 90€ cada uno",
    hours6: "Curso básico 6 horas (2-3 días)",
    // Activity translations
    windsurfPrivate: "🌊 Windsurf Privado",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privado",
    wingfoilPrivate: "🪽 Wingfoil Privado",
    wingfoilGroup: "🪽 Wingfoil Grupo",
    wingsurfPrivate: "🏄 Wingsurf Privado",
    wingsurfGroup: "🏄 Wingsurf Grupo",
    kitesurfPrivate: "🪁 Kitesurf Privado",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privado",
    // Duration translations
    duration_2h: "2 horas",
    duration_6h: "Curso básico 6 horas",
    duration_1h: "1 hora",
    perPerson: "cada uno",
    regIncluded: "inscr. €10 incluida",
    // Multi-day stay translations
    stayMultipleDays: "Quiero quedarme varios días",
    stayDuration: "¿Cuánto tiempo te quedas?",
    stay2to6days: "2-6 días",
    stay1week: "1 semana",
    stay2weeks: "2+ semanas",
    additionalDates: "Selecciona otras fechas disponibles",
    accommodationShort: "🏠 Para alojamiento: isoladeigabbiani.it (camping), hotelportopuddu.it (residencia), hotelledune.it (hotel)",
    accommodationLong: "🏡 ¡Puedes reservar nuestra casa de campo a 15 min de la escuela!",
    accommodationPrices: "💰 Habitación privada: €60/día (temporada baja Abr-May-Sep-Oct) | €100/día (temporada alta). Casa entera (máx 6 pers.): €200/día temp. baja | €400/día temp. alta. Mín. 7 días.",
    accommodationLink: "ℹ️ Info: portopollo.it",
    declarationsTitle: "Declaraciones Obligatorias",
    healthDeclaration: "Declaro estar en buenas condiciones físicas y estar en posesión de un certificado médico para la práctica deportiva no competitiva.",
    regulationsDeclaration: "Declaro conocer y aceptar el reglamento de la asociación Porto Pollo Academy A.S.D.",
    liabilityDeclaration: "Exonero a Porto Pollo Academy A.S.D. de cualquier responsabilidad por daños a terceros o a mí mismo durante la actividad.",
    privacyDeclaration: "Consiento el tratamiento de datos personales de acuerdo con el Decreto Legislativo 196/2003 y GDPR.",
    optionalTitle: "Consentimientos Opcionales (no obligatorios)",
    mediaConsent: "📸 Consiento ser filmado/fotografiado y que las imágenes puedan ser utilizadas en las redes sociales y web de la escuela.",
    newsletterConsent: "📧 Deseo recibir boletines y actualizaciones de la escuela.",
    membershipFee: "✅ Cuota de membresía €10 ya incluida en el precio",
    rentalUpsellTitle: "Alquiler de Equipo (opcional)",
    rentalUpsellDesc: "Anade 1 hora de alquiler de equipo despues de tu clase a precio reducido!",
    rentalUpsellPrice: "€20/equipo en vez de €30",
    rentalUpsellAdd: "Anadir alquiler",
    rentalEquipmentCount: "Cuantos equipos quieres alquilar?",
    rentalEquipmentSingle: "equipo",
    rentalEquipmentPlural: "equipos",
    scheduleWarning: "⚠️ Nota importante: Los horarios y días indicados son preferencias y pueden variar según las reservas existentes y las condiciones del viento. Confirmaremos la fecha y hora definitivas.",
    signatureInstructions: "Usa tu dedo o el ratón para firmar en el recuadro de abajo.",
    clearSignature: "Borrar Firma",
    signHere: "Firma aquí",
    signatureNote: "Nota: Al firmar este formulario, aceptas todas las condiciones indicadas y confirmas la veracidad de los datos introducidos.",
    back: "Atrás",
    next: "Siguiente",
    submit: "Enviar Formulario",
    sending: "Enviando...",
    successMessage: "¡Formulario enviado con éxito! Te contactaremos pronto 🏄‍♀️",
    errorRequired: "Por favor, completa todos los campos obligatorios",
    errorDeclarations: "Debes aceptar todas las declaraciones",
    errorSignature: "Por favor, firma el formulario para continuar",
    errorSubmit: "Error al enviar el formulario. Por favor, inténtalo de nuevo.",
    minorSectionTitle: "EN CASO DE MENOR — Datos del Padre/Tutor",
    minorWarning: "El participante tiene menos de 18 años — los datos del padre/tutor son obligatorios.",
    parentDataLabel: "Datos del Padre/Tutor",
    nationality: "Nacionalidad",
    birthDateShort: "Fecha de nacimiento",
    birthPlaceShort: "Lugar de nacimiento",
    provinceShort: "Prov.",
    sex: "Sexo",
    male: "Hombre",
    female: "Mujer",
    calcFiscalCode: "Calcular",
    address: "Dirección",
    cap: "CP",
    cityShort: "Ciudad",
    phoneShort: "Tel/Móvil"
  }
};

export default function RegistrationForm({ isOpen, onClose, sessionId, language = "it" }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegulations, setShowRegulations] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    fiscal_code: "",
    nationality: "Italia",
    birth_date: "",
    birth_place: "",
    residence_city: "",
    residence_address: "",
    phone: "",
    email: "",
    lesson_type: "windsurf_privato",
    num_participants: 1,
    participant_age: "",
    participant_weight: "",
    experience_level: "beginner",
    experience_details: "",
    preferred_date: "",
    preferred_time: "",
    duration: "1h_70",
    stay_multiple_days: false,
    stay_duration: "",
    additional_dates: "",
    accepts_health_declaration: false,
    accepts_regulations: false,
    accepts_liability_waiver: false,
    accepts_privacy: false,
    accepts_media_consent: false,
    accepts_newsletter: false,
    rental_equipment: false,
    rental_equipment_count: 1,
    signature_data: "",
    // In caso di minore - dati genitore/tutore
    is_minor: false,
    parent_name: "",
    parent_surname: "",
    parent_birth_date: "",
    parent_birth_place: "",
    parent_birth_province: "",
    parent_sex: "",
    parent_fiscal_code: "",
    parent_address: "",
    parent_cap: "",
    parent_province: "",
    parent_city: "",
    parent_nationality: "Italia",
    parent_email: "",
    parent_phone: "",
  });

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const t = formTranslations[language] || formTranslations.it;

  useEffect(() => {
    if (step === 4 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [step]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-set num_participants based on lesson type
      if (field === "lesson_type") {
        if (value.includes("privato") && !value.includes("semiprivato")) {
          // Private lesson = 1 participant
          updated.num_participants = 1;
        } else if (value.includes("semiprivato") || value.includes("gruppo")) {
          // Semi-private/group = minimum 2 participants
          if (updated.num_participants < 2) {
            updated.num_participants = 2;
          }
        }
        // Reset rental when switching away from wingsurf/windsurf
        if (!value.includes("wingsurf") && !value.includes("windsurf") && !value.includes("wingfoil")) {
          updated.rental_equipment = false;
          updated.rental_equipment_count = 1;
        }
      }
      
      return updated;
    });
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.closePath();
      setIsDrawing(false);
      setFormData(prev => ({ ...prev, signature_data: canvas.toDataURL("image/png") }));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setFormData(prev => ({ ...prev, signature_data: "" }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.surname || !formData.email || !formData.phone) {
      toast.error(t.errorRequired);
      return;
    }
    if (!formData.nationality) {
      toast.error("Seleziona il paese / Select country");
      return;
    }
    if ((formData.nationality === "Italia" || formData.nationality === "Italiana") && (!formData.birth_province || !formData.birth_place)) {
      toast.error("Seleziona provincia e citta' / Select province and city");
      return;
    }
    if (!formData.accepts_health_declaration || !formData.accepts_regulations || 
        !formData.accepts_liability_waiver || !formData.accepts_privacy || !formData.accepts_media_consent) {
      toast.error(t.errorDeclarations);
      return;
    }
    if (!formData.signature_data) {
      toast.error(t.errorSignature);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/registration`, {
        ...formData,
        session_id: sessionId,
        language: language
      });
      toast.success(t.successMessage);
      onClose();
      setStep(1);
      setFormData({
        name: "", surname: "", fiscal_code: "", nationality: "Italia", birth_date: "", birth_place: "", birth_province: "",
        residence_city: "", residence_address: "", phone: "", email: "",
        lesson_type: "windsurf_privato", num_participants: 1, participant_age: "", participant_weight: "", experience_level: "beginner", experience_details: "",
        preferred_date: "", preferred_time: "", duration: "1h_70",
        stay_multiple_days: false, stay_duration: "", additional_dates: "",
        accepts_health_declaration: false, accepts_regulations: false,
        accepts_liability_waiver: false, accepts_privacy: false,
        accepts_media_consent: false, accepts_newsletter: false,
        rental_equipment: false, rental_equipment_count: 1,
        signature_data: ""
      });
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t.errorSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white w-full max-w-md max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white p-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{t.title}</h2>
              <p className="text-sm text-white/80">{t.subtitle}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex gap-2 p-4 bg-gray-50">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-200">
                <div 
                  className={`h-full transition-all duration-300 ${s <= step ? "bg-teal-500" : "bg-gray-200"}`}
                  style={{ width: s <= step ? "100%" : "0%" }}
                />
              </div>
            ))}
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Step 1: Personal Data */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="font-semibold text-gray-900">{t.step1Title}</h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="name">{t.name} *</Label>
                    <Input id="name" value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Mario" />
                  </div>
                  <div>
                    <Label htmlFor="surname">{t.surname} *</Label>
                    <Input id="surname" value={formData.surname} onChange={e => handleChange("surname", e.target.value)} placeholder="Rossi" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="mario@email.com" />
                </div>

                <div>
                  <Label htmlFor="phone">{t.phone} *</Label>
                  <Input id="phone" type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+39 123 456 7890" />
                </div>

                {(formData.nationality === "Italia" || formData.nationality === "Italiana") && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="fiscal_code">{t.fiscalCode}</Label>
                      <a href="https://www.codicefiscaleonline.com/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-teal-600 hover:text-teal-800 underline">
                        Calcola codice fiscale
                      </a>
                    </div>
                    <Input id="fiscal_code" value={formData.fiscal_code} onChange={e => handleChange("fiscal_code", e.target.value)} placeholder="RSSMRA80A01H501U" />
                  </div>
                )}

                <div>
                  <Label>{t.nationality || "Paese"}</Label>
                  <Select value={formData.nationality} onValueChange={v => {
                    handleChange("nationality", v);
                    if (v !== "Italia" && v !== "Italiana") {
                      handleChange("fiscal_code", "");
                      handleChange("birth_province", "");
                      handleChange("birth_place", "");
                      handleChange("residence_city", "");
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Italia" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CSEN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="birth_date">{t.birthDate}</Label>
                    <Input id="birth_date" type="date" value={formData.birth_date} onChange={e => handleChange("birth_date", e.target.value)} />
                  </div>
                  {(formData.nationality === "Italia" || formData.nationality === "Italiana") && (
                    <div>
                      <Label>{t.birthProvince || "Provincia nascita"} *</Label>
                      <Select value={formData.birth_province || ""} onValueChange={v => { handleChange("birth_province", v); handleChange("birth_place", ""); }}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {ITALIAN_PROVINCES.map(p => <SelectItem key={p.code} value={p.code}>{p.code} - {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {(formData.nationality === "Italia" || formData.nationality === "Italiana") && (
                  <div>
                    <Label>{t.birthPlace || "Citta' di nascita"} *</Label>
                    <CityCombobox
                      value={formData.birth_place || ""}
                      onChange={v => handleChange("birth_place", v)}
                      options={ITALIAN_PROVINCES.find(p => p.code === formData.birth_province)?.cities || []}
                      placeholder="Seleziona citta'..."
                      disabled={!formData.birth_province}
                      disabledPlaceholder="Seleziona prima la provincia"
                    />
                  </div>
                )}

                {(formData.nationality === "Italia" || formData.nationality === "Italiana") && (
                  <div>
                    <Label htmlFor="residence_city">{t.residenceCity}</Label>
                    <Input id="residence_city" value={formData.residence_city} onChange={e => handleChange("residence_city", e.target.value)} placeholder="Olbia" />
                  </div>
                )}

                <div>
                  <Label htmlFor="residence_address">{t.residenceAddress}</Label>
                  <Input id="residence_address" value={formData.residence_address} onChange={e => handleChange("residence_address", e.target.value)} placeholder="Via Roma 1" />
                </div>
              </motion.div>
            )}

            {/* Step 2: Lesson & Declarations */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="font-semibold text-gray-900">{t.step2Title}</h3>
                
                <div>
                  <Label>{t.activity}</Label>
                  <Select value={formData.lesson_type} onValueChange={v => {
                    handleChange("lesson_type", v);
                    // Reset duration when activity changes
                    handleChange("duration", "");
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="windsurf_prova">🌊 Windsurf - Prova (1 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_daily">🌊 Windsurf - Daily (2 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_speedy1">🌊 Windsurf - Speedy 1 (3 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_speedy2">🌊 Windsurf - Speedy Advanced (3 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_esordienti">🌊 Windsurf - Esordienti (5 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_intensivo">🌊 Windsurf - Intensivo (7 lez. 1h30)</SelectItem>
                      <SelectItem value="windsurf_privata">🌊 Windsurf - Lezione Privata</SelectItem>
                      <SelectItem value="windsurf_giocobimbi">🌊 Windsurf - Gioco Surf (4-9 anni)</SelectItem>
                      <SelectItem value="funboard_prova">🌊 Funboard - Prova (1 lez. 1h30)</SelectItem>
                      <SelectItem value="funboard_f1">🌊 Funboard 1 (3 lez. 1h30)</SelectItem>
                      <SelectItem value="funboard_full">🌊 Funboard Full (5 lez. 1h30)</SelectItem>
                      <SelectItem value="funboard_advanced">🌊 Funboard Advanced (3 lez. 1h30)</SelectItem>
                      <SelectItem value="funboard_intensivo">🌊 Funboard Intensivo (7 lez. 1h30)</SelectItem>
                      <SelectItem value="windfoil_1">🌊 Wind Foil 1 (2 lez. 1h30)</SelectItem>
                      <SelectItem value="windfoil_2">🌊 Wind Foil 2 (3 lez. 1h30)</SelectItem>
                      <SelectItem value="wingfoil_w1">🪽 Wingfoil - Principiante W1 (5 lez. 1.5h)</SelectItem>
                      <SelectItem value="wingfoil_w2">🪽 Wingfoil - Avanzato W2 (3 lez. 1.5h)</SelectItem>
                      <SelectItem value="wingfoil_w3">🪽 Wingfoil - Pro W3 (2 lez. 1.5h)</SelectItem>
                      <SelectItem value="wingfoil_singola">🪽 Wingfoil - Lezione Singola</SelectItem>
                      <SelectItem value="wingfoil_privata">🪽 Wingfoil - Lezione Privata</SelectItem>
                      <SelectItem value="kitesurf_privata">🪁 Kitesurf - Lezione Privata (1h)</SelectItem>
                      <SelectItem value="kitesurf_6h">🪁 Kitesurf - Corso 6 ore private</SelectItem>
                      <SelectItem value="kitesurf_prima_terra">🪁 Kitesurf - Prima lezione a terra (max 3, condivisa)</SelectItem>
                      <SelectItem value="kitesurf_alta_stagione">🪁 Kitesurf - Alta stagione con gommone (lug-set)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Price display - right after activity */}
                {formData.lesson_type && (
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <span className="font-bold text-teal-800 text-lg">
                      {formData.lesson_type === "windsurf_prova" && "€55"}
                      {formData.lesson_type === "windsurf_daily" && "€110"}
                      {formData.lesson_type === "windsurf_speedy1" && "€160"}
                      {formData.lesson_type === "windsurf_speedy2" && "€160"}
                      {formData.lesson_type === "windsurf_esordienti" && "€265"}
                      {formData.lesson_type === "windsurf_intensivo" && "€350"}
                      {formData.lesson_type === "windsurf_privata" && "€70/h - €125/2h"}
                      {formData.lesson_type === "windsurf_giocobimbi" && "da €25 a €150"}
                      {formData.lesson_type === "funboard_prova" && "€60"}
                      {formData.lesson_type === "funboard_f1" && "€168"}
                      {formData.lesson_type === "funboard_full" && "€250"}
                      {formData.lesson_type === "funboard_advanced" && "€168"}
                      {formData.lesson_type === "funboard_intensivo" && "€345"}
                      {formData.lesson_type === "windfoil_1" && "€140"}
                      {formData.lesson_type === "windfoil_2" && "€205"}
                      {formData.lesson_type === "wingfoil_w1" && "€380"}
                      {formData.lesson_type === "wingfoil_w2" && "€250"}
                      {formData.lesson_type === "wingfoil_w3" && "€160"}
                      {formData.lesson_type === "wingfoil_singola" && "€70 (W1) / €85 (W2-W3)"}
                      {formData.lesson_type === "wingfoil_privata" && "€80-100/h"}
                      {formData.lesson_type === "kitesurf_privata" && "€90/h"}
                      {formData.lesson_type === "kitesurf_6h" && "€450 (€75/h)"}
                      {formData.lesson_type === "kitesurf_prima_terra" && "€60/h a persona (max 3)"}
                      {formData.lesson_type === "kitesurf_alta_stagione" && "€100/h — solo lug-set, su richiesta"}
                    </span>
                    <span className="text-xs text-teal-600 ml-2">(+ tessera €10 inclusa)</span>
                  </div>
                )}

                {/* Age and Weight */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t.participantAge}</Label>
                    <Input
                      type="number"
                      min="7"
                      max="99"
                      value={formData.participant_age}
                      onChange={e => handleChange("participant_age", e.target.value)}
                      placeholder={t.agePlaceholder}
                    />
                    {formData.participant_age && parseInt(formData.participant_age) >= 7 && parseInt(formData.participant_age) <= 14 && (
                      <p className="text-xs text-green-600 mt-1">🎉 Sconto ragazzi: -€5 ogni 2 ore!</p>
                    )}
                  </div>
                  <div>
                    <Label>{t.participantWeight} <span className="text-gray-400 text-xs">(opzionale)</span></Label>
                    <Input
                      type="number"
                      min="20"
                      max="150"
                      value={formData.participant_weight}
                      onChange={e => handleChange("participant_weight", e.target.value)}
                      placeholder={t.weightPlaceholder}
                    />
                  </div>
                </div>

                {/* IN CASO DI MINORE — Dati Genitore/Tutore */}
                <div className="p-3 rounded-lg border border-teal-200 bg-teal-50/50">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_minor}
                      onChange={e => handleChange("is_minor", e.target.checked)}
                      className="mt-1 w-4 h-4 accent-teal-600"
                      data-testid="is-minor-checkbox"
                    />
                    <span className="text-sm font-semibold text-teal-900">
                      {t.minorSectionTitle}
                    </span>
                  </label>
                  {formData.participant_age && parseInt(formData.participant_age) > 0 && parseInt(formData.participant_age) < 18 && !formData.is_minor && (
                    <p className="text-xs text-amber-700 mt-1 ml-6">
                      ⚠ {t.minorWarning}
                    </p>
                  )}

                  {formData.is_minor && (() => {
                    const pIsItalian = formData.parent_nationality === "Italia" || formData.parent_nationality === "Italiana";
                    return (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>{t.surname}</Label>
                            <Input value={formData.parent_surname} onChange={e => handleChange("parent_surname", e.target.value)} placeholder="Rossi" />
                          </div>
                          <div>
                            <Label>{t.name}</Label>
                            <Input value={formData.parent_name} onChange={e => handleChange("parent_name", e.target.value)} placeholder="Mario" />
                          </div>
                        </div>
                        <div>
                          <Label>{t.nationality}</Label>
                          <Select value={formData.parent_nationality} onValueChange={v => {
                            handleChange("parent_nationality", v);
                            if (v !== "Italia" && v !== "Italiana") {
                              handleChange("parent_fiscal_code", "");
                              handleChange("parent_birth_province", "");
                              handleChange("parent_birth_place", "");
                              handleChange("parent_cap", "");
                              handleChange("parent_province", "");
                              handleChange("parent_city", "");
                            }
                          }}>
                            <SelectTrigger><SelectValue placeholder="Italia" /></SelectTrigger>
                            <SelectContent className="max-h-60">
                              {CSEN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className={pIsItalian ? "grid grid-cols-3 gap-3" : ""}>
                          <div>
                            <Label>{t.birthDateShort}</Label>
                            <Input type="date" value={formData.parent_birth_date} onChange={e => handleChange("parent_birth_date", e.target.value)} />
                          </div>
                          {pIsItalian && (
                            <>
                              <div>
                                <Label>{t.provinceShort}</Label>
                                <Select value={formData.parent_birth_province} onValueChange={v => { handleChange("parent_birth_province", v); handleChange("parent_birth_place", ""); }}>
                                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    {ITALIAN_PROVINCES.map(p => <SelectItem key={p.code} value={p.code}>{p.code} - {p.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>{t.birthPlaceShort}</Label>
                                <CityCombobox
                                  value={formData.parent_birth_place}
                                  onChange={v => handleChange("parent_birth_place", v)}
                                  options={ITALIAN_PROVINCES.find(p => p.code === formData.parent_birth_province)?.cities || []}
                                  placeholder="—"
                                  disabled={!formData.parent_birth_province}
                                  disabledPlaceholder={t.provinceShort}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <div className={pIsItalian ? "grid grid-cols-2 gap-3" : ""}>
                          <div>
                            <Label>{t.sex}</Label>
                            <Select value={formData.parent_sex} onValueChange={v => handleChange("parent_sex", v)}>
                              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="M">{t.male}</SelectItem>
                                <SelectItem value="F">{t.female}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {pIsItalian && (
                            <div>
                              <div className="flex items-center justify-between">
                                <Label>{t.fiscalCode}</Label>
                                <a href="https://www.codicefiscaleonline.com/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-teal-600 hover:text-teal-800 underline">{t.calcFiscalCode}</a>
                              </div>
                              <Input value={formData.parent_fiscal_code} onChange={e => handleChange("parent_fiscal_code", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" />
                            </div>
                          )}
                        </div>
                        <div>
                          <Label>{t.address}</Label>
                          <Input value={formData.parent_address} onChange={e => handleChange("parent_address", e.target.value)} placeholder="Via Roma 1" />
                        </div>
                        {pIsItalian && (
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label>{t.cap}</Label>
                              <Input value={formData.parent_cap} onChange={e => handleChange("parent_cap", e.target.value)} placeholder="07020" />
                            </div>
                            <div>
                              <Label>{t.provinceShort}</Label>
                              <Input value={formData.parent_province} onChange={e => handleChange("parent_province", e.target.value.toUpperCase())} placeholder="SS" maxLength={2} />
                            </div>
                            <div>
                              <Label>{t.cityShort}</Label>
                              <Input value={formData.parent_city} onChange={e => handleChange("parent_city", e.target.value)} placeholder="Palau" />
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>{t.email}</Label>
                            <Input type="email" value={formData.parent_email} onChange={e => handleChange("parent_email", e.target.value)} placeholder="email@example.com" />
                          </div>
                          <div>
                            <Label>{t.phoneShort}</Label>
                            <Input type="tel" value={formData.parent_phone} onChange={e => handleChange("parent_phone", e.target.value)} placeholder="+39 351..." />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Number of participants - only for semi-private/group */}
                {(formData.lesson_type.includes("semiprivato") || formData.lesson_type.includes("gruppo")) && (
                  <div>
                    <Label>{t.numParticipants}</Label>
                    <div className="flex gap-2 mt-2">
                      {[2, 3, 4, 6].map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => handleChange("num_participants", num)}
                          className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                            formData.num_participants === num
                              ? "border-teal-500 bg-teal-50 text-teal-700"
                              : "border-gray-200 hover:border-gray-300 text-gray-600"
                          }`}
                        >
                          {num} 👥
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Experience Level */}
                <div className="space-y-3">
                  <Label>{t.level}</Label>
                  <div className="space-y-2">
                    <div 
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.experience_level === "beginner" 
                          ? "border-teal-500 bg-teal-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => {
                        handleChange("experience_level", "beginner");
                        handleChange("experience_details", "");
                      }}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.experience_level === "beginner" ? "border-teal-500" : "border-gray-300"
                      }`}>
                        {formData.experience_level === "beginner" && (
                          <div className="w-3 h-3 rounded-full bg-teal-500" />
                        )}
                      </div>
                      <span className="text-sm font-medium">🌱 {t.levelBeginner}</span>
                    </div>
                    
                    <div 
                      className={`flex flex-col gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.experience_level === "experienced" 
                          ? "border-teal-500 bg-teal-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleChange("experience_level", "experienced")}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.experience_level === "experienced" ? "border-teal-500" : "border-gray-300"
                        }`}>
                          {formData.experience_level === "experienced" && (
                            <div className="w-3 h-3 rounded-full bg-teal-500" />
                          )}
                        </div>
                        <span className="text-sm font-medium">🏄 {t.levelExperienced}</span>
                      </div>
                      {formData.experience_level === "experienced" && (
                        <Input
                          value={formData.experience_details}
                          onChange={e => handleChange("experience_details", e.target.value)}
                          placeholder={t.levelDetails}
                          className="mt-2 text-sm"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t.preferredDate}</Label>
                    <Input type="date" value={formData.preferred_date} onChange={e => handleChange("preferred_date", e.target.value)} />
                  </div>
                  <div>
                    <Label>{t.time}</Label>
                    <Select value={formData.preferred_time} onValueChange={v => handleChange("preferred_time", v)}>
                      <SelectTrigger><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                        <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                        <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
                  {t.scheduleWarning}
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-3">{t.declarationsTitle}</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="health" 
                        checked={formData.accepts_health_declaration}
                        onCheckedChange={c => handleChange("accepts_health_declaration", c)}
                      />
                      <label htmlFor="health" className="text-sm text-gray-700 cursor-pointer">
                        {t.healthDeclaration}
                      </label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="regulations" 
                        checked={formData.accepts_regulations}
                        onCheckedChange={c => handleChange("accepts_regulations", c)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <label htmlFor="regulations" className="text-sm text-gray-700 cursor-pointer">
                          {t.regulationsDeclaration}
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowRegulations(true)}
                          className="flex items-center gap-1 mt-1 text-xs text-teal-600 hover:text-teal-700 font-medium"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {language === "it" ? "Leggi il regolamento completo" : 
                           language === "en" ? "Read full regulations" :
                           language === "fr" ? "Lire le règlement complet" :
                           language === "de" ? "Vollständige Regeln lesen" :
                           "Leer reglamento completo"}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="liability" 
                        checked={formData.accepts_liability_waiver}
                        onCheckedChange={c => handleChange("accepts_liability_waiver", c)}
                      />
                      <label htmlFor="liability" className="text-sm text-gray-700 cursor-pointer">
                        {t.liabilityDeclaration}
                      </label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="privacy" 
                        checked={formData.accepts_privacy}
                        onCheckedChange={c => handleChange("accepts_privacy", c)}
                      />
                      <label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
                        {t.privacyDeclaration}
                      </label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="mediaConsent" 
                        checked={formData.accepts_media_consent}
                        onCheckedChange={c => handleChange("accepts_media_consent", c)}
                      />
                      <label htmlFor="mediaConsent" className="text-sm text-gray-700 cursor-pointer">
                        {t.mediaConsent}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Optional Consents Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-3">{t.optionalTitle}</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="newsletter" 
                        checked={formData.accepts_newsletter}
                        onCheckedChange={c => handleChange("accepts_newsletter", c)}
                      />
                      <label htmlFor="newsletter" className="text-sm text-blue-700 cursor-pointer">
                        {t.newsletterConsent}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 p-3 rounded-lg text-sm text-teal-800">
                  <strong>{t.membershipFee}</strong>
                </div>
              </motion.div>
            )}

            {/* Step 3: Preview */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Check className="w-5 h-5 text-teal-600" />
                  Anteprima Modulo
                </h3>
                <p className="text-xs text-gray-500">Verifica i dati prima di firmare e inviare</p>

                <div className="bg-white border rounded-lg p-4 space-y-3 text-sm shadow-sm max-h-[50vh] overflow-y-auto">
                  <div className="text-center border-b pb-2">
                    <p className="font-bold">MODULO DI ISCRIZIONE</p>
                    <p className="text-xs text-gray-500">Circolo Portopollo Academy A.S.D.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <p><span className="text-gray-500">Nome:</span> <strong>{formData.name}</strong></p>
                    <p><span className="text-gray-500">Cognome:</span> <strong>{formData.surname}</strong></p>
                    <p><span className="text-gray-500">Email:</span> <strong>{formData.email}</strong></p>
                    <p><span className="text-gray-500">Telefono:</span> <strong>{formData.phone}</strong></p>
                    {formData.fiscal_code && <p><span className="text-gray-500">C.F.:</span> <strong>{formData.fiscal_code}</strong></p>}
                    <p><span className="text-gray-500">Paese:</span> <strong>{formData.nationality}</strong></p>
                    {formData.birth_date && <p><span className="text-gray-500">Data nascita:</span> <strong>{formData.birth_date.split('-').reverse().join('/')}</strong></p>}
                    {formData.birth_place && <p><span className="text-gray-500">Luogo:</span> <strong>{formData.birth_place}</strong></p>}
                    {formData.residence_city && <p><span className="text-gray-500">Citta':</span> <strong>{formData.residence_city}</strong></p>}
                    {formData.participant_age && <p><span className="text-gray-500">Eta':</span> <strong>{formData.participant_age}</strong></p>}
                    {formData.participant_weight && <p><span className="text-gray-500">Peso:</span> <strong>{formData.participant_weight} kg</strong></p>}
                  </div>

                  <div className="border-t pt-2">
                    <p className="font-semibold text-xs text-gray-800 mb-1">Attivita':</p>
                    <p className="text-xs"><strong>{formData.lesson_type.replace(/_/g, ' ')}</strong></p>
                    {formData.preferred_date && <p className="text-xs text-gray-600">Data: {formData.preferred_date.split('-').reverse().join('/')} {formData.preferred_time && `| Orario: ${formData.preferred_time}`}</p>}
                    {formData.experience_level && <p className="text-xs text-gray-600">Livello: {formData.experience_level === 'beginner' ? 'Principiante' : 'Con esperienza'}</p>}
                  </div>

                  <div className="border-t pt-2">
                    <p className="font-semibold text-xs text-gray-800 mb-1">Dichiarazioni:</p>
                    <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                      <li>Buone condizioni fisiche + certificato medico: <strong className="text-green-700">Si</strong></li>
                      <li>Regolamento accettato: <strong className="text-green-700">Si</strong></li>
                      <li>Privacy GDPR: <strong className="text-green-700">Si</strong></li>
                    </ul>
                  </div>

                  <div className="border-t pt-2 text-center">
                    <p className="text-xs text-gray-500">Quota associativa <strong>10,00 EUR</strong> inclusa</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1" size="sm">
                    Modifica
                  </Button>
                  <Button onClick={() => setStep(4)} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" size="sm">
                    Procedi alla Firma
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Signature */}
            {step === 4 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                {/* Deposit Status Badge */}
                <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-3 flex items-center gap-3">
                  <div className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-bold">
                    💰 DEP. NON PAGATO
                  </div>
                  <span className="text-sm text-amber-800">
                    {language === "it" ? "Il deposito verrà richiesto dopo la conferma" :
                     language === "en" ? "Deposit will be requested after confirmation" :
                     language === "fr" ? "L'acompte sera demandé après confirmation" :
                     language === "de" ? "Anzahlung wird nach Bestätigung angefordert" :
                     "El depósito se solicitará después de la confirmación"}
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Pen className="w-5 h-5" />
                  {t.step3Title}
                </h3>
                
                <p className="text-sm text-gray-600">
                  {t.signatureInstructions}
                </p>

                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={350}
                    height={200}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-gray-400">{t.signHere}</p>
                    </div>
                  )}
                </div>

                <Button
                  variant="outline"
                  onClick={clearSignature}
                  className="w-full"
                  disabled={!hasSignature}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t.clearSignature}
                </Button>

                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                  <strong>{t.signatureNote}</strong>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="p-4 border-t bg-gray-50 flex gap-3">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
                {t.back}
              </Button>
            )}
            
            {step < 4 ? (
              <Button 
                onClick={() => {
                  // Validate step 2: all declarations must be checked
                  if (step === 2 && (!formData.accepts_health_declaration || !formData.accepts_regulations || 
                      !formData.accepts_liability_waiver || !formData.accepts_privacy)) {
                    toast.error(t.errorDeclarations);
                    return;
                  }
                  setStep(s => s + 1);
                }} 
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={step === 1 && (!formData.name || !formData.surname || !formData.email || !formData.phone)}
              >
                {step === 3 ? "Procedi alla Firma" : t.next}
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                disabled={isSubmitting || !hasSignature}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.sending}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t.submit}
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Regulations Modal */}
      <AnimatePresence>
        {showRegulations && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowRegulations(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-4 text-white">
                <h3 className="text-lg font-bold">{regulationsContent[language]?.title || regulationsContent.it.title}</h3>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
                {(regulationsContent[language]?.rules || regulationsContent.it.rules).map((rule) => (
                  <div key={rule.number} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className="bg-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {rule.number}
                      </span>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {rule.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 border-t bg-gray-50">
                <Button
                  onClick={() => setShowRegulations(false)}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                >
                  {regulationsContent[language]?.close || regulationsContent.it.close}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
